import { Component, inject } from '@angular/core';
import { BackgroundColorCardComponent } from '../shared/background-color-card/background-color-card.component';
import { TextColorCardComponent } from '../shared/text-color-card/text-color-card.component';
import { IconColorCardComponent } from '../shared/icon-color-card/icon-color-card.component';
import { OutlineColorCardComponent } from '../shared/outline-color-card/outline-color-card.component';
import { ContainerColorCardComponent } from '../shared/container-color-card/container-color-card.component';
import { ElevatedContainerColorCardComponent } from '../shared/elevated-container-color-card/elevated-container-color-card.component';
import { TypographyTokenCardComponent } from '../shared/typography-token-card/typography-token-card.component';
import { OverlayColorCardComponent } from '../shared/overlay-color-card/overlay-color-card.component';
import { DataVisColorChartCardComponent } from '../shared/data-vis-color-chart-card/data-vis-color-chart-card.component';
import { StylingGetStartedComponent } from '../styling-get-started/styling-get-started.component';
import { ThemeToggleButtonComponent } from 'src/app/shared/components/theme-toggle-button/theme-toggle-button.component';
import { CodeExampleHeaderComponent } from 'src/app/shared/components/code-example/code-example-header.component';
import { UilabMatTabsModule } from '@uilab/ng-mat-components';
import {
    StylingColorItem,
    StylingTypographyItem,
    StylingTokensService,
} from '../styling-tokens/styling-tokens.service';
import { HeaderComponent } from '../../../shared/components/header/header.component';

interface StylingDataVisTintItem {
    readonly tint: string;
    readonly label: string;
}

@Component({
    selector: 'avd-styling-page',
    standalone: true,
    templateUrl: './styling-page.component.html',
    styleUrls: ['./styling-page.component.scss'],
    imports: [
        BackgroundColorCardComponent,
        TextColorCardComponent,
        IconColorCardComponent,
        OutlineColorCardComponent,
        ContainerColorCardComponent,
        ElevatedContainerColorCardComponent,
        TypographyTokenCardComponent,
        OverlayColorCardComponent,
        DataVisColorChartCardComponent,
        StylingGetStartedComponent,
        ThemeToggleButtonComponent,
        CodeExampleHeaderComponent,
        UilabMatTabsModule,
        HeaderComponent,
    ],
})
export class StylingPageComponent {
    private readonly stylingTokensService = inject(StylingTokensService);

    readonly packageName: string = '@uilab/styling';
    readonly storybookLink: string = 'https://web.aveva.design/tokens/';
    readonly repoLink: string = 'https://dev.azure.com/AVEVA-VSTS/UX/_git/web';
    readonly backgroundSection: {
        readonly title: string;
        readonly items: readonly StylingColorItem[];
    } = {
        title: 'Background colors',
        items: this.stylingTokensService.getColorTokens({
            lightPath: ['color', 'background'],
            inversePath: ['color-inverse', 'background'],
            labelPrefix: 'Background',
        }),
    };
    readonly appBackgroundSection: {
        readonly title: string;
        readonly items: readonly StylingColorItem[];
    } = {
        title: 'App background color',
        items: this.stylingTokensService.getColorTokens({
            lightPath: ['color', 'app-background', 'default'],
            labelFormatter: () => 'App background',
        }),
    };
    readonly textSection: {
        readonly title: string;
        readonly items: readonly StylingColorItem[];
    } = {
        title: 'Text colors',
        items: this.stylingTokensService
            .getTokenGroupKeys(['color', 'text'])
            .flatMap((palette) => {
                const paletteLabel = this.formatLabel([palette]);

                return this.stylingTokensService.getColorTokens({
                    lightPath: ['color', 'text', palette],
                    inversePath: ['color-inverse', 'text', palette],
                    labelFormatter: (path) =>
                        `${paletteLabel} - ${this.formatLabel(path)}`,
                });
            }),
    };
    readonly iconSection: {
        readonly title: string;
        readonly items: readonly StylingColorItem[];
    } = {
        title: 'Icon colors',
        items: this.stylingTokensService
            .getTokenGroupKeys(['color', 'icon'])
            .flatMap((palette) => {
                const paletteLabel = this.formatLabel([palette]);

                return this.stylingTokensService.getColorTokens({
                    lightPath: ['color', 'icon', palette],
                    inversePath: ['color-inverse', 'icon', palette],
                    labelFormatter: (path) =>
                        `${paletteLabel} - ${this.formatLabel(path)}`,
                });
            }),
    };

    readonly outlineSection: {
        readonly title: string;
        readonly items: readonly StylingColorItem[];
    } = {
        title: 'Outline colors',
        items: this.stylingTokensService
            .getTokenGroupKeys(['color', 'outline'])
            .flatMap((palette) => {
                const paletteLabel = this.formatLabel([palette]);

                return this.stylingTokensService.getColorTokens({
                    lightPath: ['color', 'outline', palette],
                    inversePath: ['color-inverse', 'outline', palette],
                    labelFormatter: (path) =>
                        `${paletteLabel} - ${this.formatLabel(path)}`,
                });
            }),
    };

    readonly containerSection: {
        readonly title: string;
        readonly regularTitle: string;
        readonly regularItems: readonly StylingColorItem[];
        readonly solidTitle: string;
        readonly solidItems: readonly StylingColorItem[];
    } = {
        title: 'Container colors',
        regularTitle: 'Regular',
        regularItems: this.stylingTokensService
            .getTokenGroupKeys(['color', 'container', 'regular'])
            .flatMap((palette) => {
                const paletteLabel = this.formatLabel([palette]);

                return this.stylingTokensService.getColorTokens({
                    lightPath: ['color', 'container', 'regular', palette],
                    inversePath: [
                        'color-inverse',
                        'container',
                        'regular',
                        palette,
                    ],
                    labelFormatter: (path) =>
                        `${paletteLabel} - ${this.formatLabel(path)}`,
                });
            }),
        solidTitle: 'Solid',
        solidItems: this.stylingTokensService
            .getTokenGroupKeys(['color', 'container', 'solid'])
            .flatMap((palette) => {
                const paletteLabel = this.formatLabel([palette]);

                return this.stylingTokensService.getColorTokens({
                    lightPath: ['color', 'container', 'solid', palette],
                    inversePath: [
                        'color-inverse',
                        'container',
                        'solid',
                        palette,
                    ],
                    labelFormatter: (path) =>
                        `${paletteLabel} - ${this.formatLabel(path)}`,
                });
            }),
    };
    readonly elevatedContainerSection: {
        readonly title: string;
        readonly items: readonly StylingColorItem[];
    } = {
        title: 'Elevated container colors',
        items: this.stylingTokensService.getColorTokens({
            lightPath: ['color', 'container', 'elevated'],
            inversePath: ['color-inverse', 'container', 'elevated'],
            includeBoxShadowVariable: true,
            labelPrefix: 'Elevated',
        }),
    };
    readonly typographySection: {
        readonly defaultTitle: string;
        readonly defaultItems: readonly StylingTypographyItem[];
        readonly editorialTitle: string;
        readonly editorialItems: readonly StylingTypographyItem[];
        readonly appTitle: string;
        readonly appItems: readonly StylingTypographyItem[];
    } = {
        defaultTitle: 'Default tokens',
        defaultItems: this.stylingTokensService.getTypographyTokens({
            path: ['typography-system'],
            tokenPathDepth: 1,
            labelPrefix: 'System',
        }),
        editorialTitle: 'Editorial tokens',
        editorialItems: this.stylingTokensService.getTypographyTokens({
            path: ['typography-system', 'editorial'],
            labelPrefix: 'Editorial',
        }),
        appTitle: 'App tokens',
        appItems: this.stylingTokensService.getTypographyTokens({
            path: ['typography-system', 'app'],
            labelPrefix: 'App',
        }),
    };
    readonly overlaySection: {
        readonly title: string;
        readonly items: readonly StylingColorItem[];
    } = {
        title: 'Overlay colors',
        items: this.stylingTokensService
            .getTokenGroupKeys(['color', 'overlay'])
            .flatMap((palette) => {
                const paletteLabel = this.formatLabel([palette]);

                return this.stylingTokensService.getColorTokens({
                    lightPath: ['color', 'overlay', palette],
                    inversePath: ['color-inverse', 'overlay', palette],
                    labelFormatter: (path) =>
                        `${paletteLabel} - ${this.formatLabel(path)}`,
                });
            }),
    };
    readonly dataVisSection: {
        readonly title: string;
        readonly items: readonly StylingDataVisTintItem[];
    } = {
        title: 'Data visualisation colors',
        items: this.getDataVisTintItems(),
    };

    private formatLabel(path: readonly string[]): string {
        const value = path.join(' ').replace(/-/g, ' ');
        return value.replace(/\b\w/g, (char) => char.toUpperCase());
    }

    private getDataVisTintItems(): readonly StylingDataVisTintItem[] {
        const tintKeys = this.stylingTokensService.getTokenGroupKeys([
            'color',
            'data-vis',
        ]);
        const preferredOrder = [
            'opaque',
            'tint-80',
            'tint-60',
            'tint-40',
            'tint-20',
        ];
        const sortedTintKeys = [
            ...preferredOrder.filter((tint) => tintKeys.includes(tint)),
            ...tintKeys.filter((tint) => !preferredOrder.includes(tint)),
        ];

        return sortedTintKeys.map((tint) => ({
            tint,
            label: this.formatLabel(['data-vis', tint]),
        }));
    }
}
